// Copyright (c) Docugami, Inc. All rights reserved.

import { Dropdown, IDropdownOption, IDropdownStyles, IStyle } from "@fluentui/react";
import { userFontFamily } from "components/common/Fonts";
import { OverflowTooltipText } from "components/common/OverflowText";
import { DocumentBindingsConfirmationButton } from "components/common/ReportBindingConfirmationButtons";
import { appBackgroundGrey } from "components/shell/presentation/AppStyles";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useAppSelector } from "hooks/redux";
import { useMemo } from "react";
import { ReportDocument, selectOrderedReportEntryIds, selectReportDocumentId, selectReportDocumentsView, selectReportViewMode } from "store/reports/selectors";
import styled from "styled-components";

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: right;
    max-width: 337px;
`

const OptionRoot = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    gap: 5px;
`;

const commonFontStyling: IStyle = {
    fontFamily: userFontFamily,
    fontSize: '14px',
    fontWeight: 500,
};

const dropdownStyles: Partial<IDropdownStyles> = {
    dropdown: {
        width: '100%',
    },
    root: {
        width: '100%'
    },
    title: {
        background: appBackgroundGrey,
        border: 'none',
        ...commonFontStyling,
        height: 18,
        minHeight: 18,
        lineHeight: '18px',
        paddingTop: 0,
        paddingBottom: 0,
    },
    caretDownWrapper: {
        height: 18,
        minHeight: 18,
        lineHeight: '18px',
        paddingTop: 0,
        paddingBottom: 0,
    },
    dropdownItemsWrapper: {
        maxWidth: '60ch',
    },
    dropdownItem: {
        ...commonFontStyling,
    },
    dropdownItemSelected: {
        ...commonFontStyling,
    }
}

interface DocumentPickerProps {
    className?: string,
}

const docToOption = (doc: ReportDocument): IDropdownOption<ReportDocument> => ({
    key: doc.folderId,
    text: `${doc.index + 1}. ${doc.clusterDocument.filename}`,
    data: doc,
    title: doc.clusterDocument.filename,
    disabled: !doc.clusterDocument.hasCpml
});

export const DocumentPicker = ({ className }: DocumentPickerProps) => {
    const navService = useNavigationService()
    const folderId = useAppSelector(selectReportDocumentId);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);
    const documentsView = useAppSelector(selectReportDocumentsView);
    const currentDocumentName = useAppSelector(state => state.documents.indexed[folderId]?.clusterDocument.filename);
    const currentViewMode = useAppSelector(selectReportViewMode);

    const documentOptions: IDropdownOption<ReportDocument>[] = useMemo(() => {
        return documentsView?.ids.map(id => docToOption(documentsView.entities[id]!)) ?? [];
    }, [documentsView]);

    const onDocumentChange = (event: React.FormEvent<HTMLDivElement>, option: IDropdownOption<ReportDocument> | undefined) => {
        if (!!option && !!option.key) {
            navService.navToReportPage(
                NavigationType.ReplaceCurrentPage,
                {
                    mode: currentViewMode,
                    documentId: option?.key.toString()
                });
        }
    };

    const onRenderOption = (option?: IDropdownOption<ReportDocument>, defaultRender?: (option?: IDropdownOption<ReportDocument>) => JSX.Element | null): JSX.Element => {
        return (
            <OptionRoot>
                {defaultRender?.(option)}
                {!!option?.data && !option.disabled && !!entryIds?.length && (
                    <DocumentBindingsConfirmationButton folderId={option.data.folderId} entryIds={entryIds} readOnly />
                )}
            </OptionRoot>
        );
    };

    return (
        <Container className={className}>
            <OverflowTooltipText content={currentDocumentName}>
                <Dropdown
                    dropdownWidth={'auto'}
                    placeholder="Select a document"
                    ariaLabel="document picker"
                    styles={dropdownStyles}
                    options={documentOptions}
                    selectedKey={folderId}
                    onChange={onDocumentChange}
                    onRenderOption={onRenderOption}
                />
            </OverflowTooltipText>
        </Container>
    )
}
